import React from 'react';
import { BookOpen, Image, Sparkles, Share2 } from 'lucide-react';
import img1 from "../images/JourneyLogging.png";
import img2 from "../images/PhotoCuration.png";
import img3 from "../images/Storytelling.png";
import img4 from "../images/DigitalScarpBooks.png";


export default function Highlights() {
  const highlights = [
    {
      title: 'Automatic Journey Log',
      description: 'Sarayu automatically notes your stops, routes, and even weather conditions, giving you a complete record of your adventure.',
      details: [
        'Automatic stop and route tracking',
        'Weather condition logging',
        'Timeline creation without effort',
        'Complete adventure records',
      ],
      icon: <BookOpen className="w-8 h-10 text-indigo-600" />,
      image: img1,
    },
    {
      title: 'Smart Photo Curation',
      description: 'Its on-device AI intelligently suggests your best photos, saving you time and ensuring no precious moment is missed.',
      details: [
        'On-device AI for privacy and speed',
        'Intelligent best photo selection',
        'Time-saving automatic curation',
        'Never miss precious moments',
      ],
      icon: <Image className="w-8 h-10 text-emerald-600" />,
      image: img2,
    },
    {
      title: 'Generative AI Storytelling',
      description: 'Sarayu weaves your travel data into a beautiful narrative, complete with maps, curated photos, and highlights, creating a truly memorable story.',
      details: [
        'Beautiful narrative generation',
        'Integrated maps and photos',
        'Automatic highlight compilation',
        'Memorable story creation',
      ],
      icon: <Sparkles className="w-8 h-10 text-pink-600" />,
      image: img3,
    },
    {
      title: 'Digital Scrapbooks',
      description: 'Sarayu automatically creates a beautiful digital scrapbook of your trip, so you can treasure every moment without lifting a finger.',
      details: [
        'Completely automatic creation',
        'Beautiful visual layouts',
        'One-click sharing',
        'Collaborative albums with friends',
      ],
      icon: <Share2 className="w-8 h-10 text-blue-600" />,
      image: img4,
    },
  ];


  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative py-20 pt-32 bg-gradient-to-br from-indigo-50 via-background to-emerald-50 heroimg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 animate-slideUp">
            Your AI Travel Photographer
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto animate-slideUp" style={{ animationDelay: '0.1s' }}>
            Don't just take a trip. Treasure it. Without lifting a finger.
          </p>
        </div>
      </section>


      {/* Highlights Sections */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {highlights.map((highlight, index) => (
            <div
              key={index}
              className={`mb-20 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center animate-slideUp`}
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              {/* Content */}
              <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                <h2 className="flex flex-row text-4xl gap-3 font-bold text-gray-900 mb-4">{highlight.icon}{highlight.title}</h2>
                <p className="text-lg text-gray-600 mb-8">{highlight.description}</p>


                {/* Details List */}
                <div className="space-y-4">
                  {highlight.details.map((detail, idx) => (
                    <div key={idx} className="flex items-start space-x-3">
                      <div className="flex-shrink-0 mt-1">
                        <div className="flex items-center justify-center h-6 w-6 rounded-full bg-emerald-100">
                          <svg
                            className="h-4 w-4 text-emerald-600"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </div>
                      </div>
                      <p className="text-gray-700 font-medium">{detail}</p>
                    </div>
                  ))}
                </div>
              </div>


              {/* Visual */}
              <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                <div className="glass rounded-3xl h-96 flex items-center justify-center opacity-75 overflow-hidden group">
                  
                    <img src={highlight.image} alt={highlight.title} className="w-full h-full object-cover rounded-3xl transition-all duration-500 ease-in-out group-hover:scale-110 group-hover:shadow-4xl" />
                 
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>


      {/* Retention & Shareability Section */}
      <section className="py-20 bg-white bgsecimg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Secret Weapon</h2>
            <p className="text-xl text-gray-600">
              Memory capture drives retention and shareability
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {/* Retention */}
            <div className="glass rounded-2xl p-8 card-hover">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Retention Through Memories</h3>
              <p className="text-gray-600 mb-6">
                Users return to relive their experiences. Every scrapbook, every curated photo, and every AI-generated story deepens their connection with Sarayu.
              </p>
              <ul className="space-y-3">
                <li className="flex items-center space-x-3">
                  <span className="text-emerald-600">✓</span>
                  <span className="text-gray-700">Emotional connection to memories</span>
                </li>
                <li className="flex items-center space-x-3">
                  <span className="text-emerald-600">✓</span>
                  <span className="text-gray-700">Lifetime access to adventures</span>
                </li>
                <li className="flex items-center space-x-3">
                  <span className="text-emerald-600">✓</span>
                  <span className="text-gray-700">Cloud-based automatic backups</span>
                </li>
              </ul>
            </div>


            {/* Shareability */}
            <div className="glass rounded-2xl p-8 card-hover">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Viral Shareability</h3>
              <p className="text-gray-600 mb-6">
                Beautiful, AI-crafted stories are made to be shared. Every share is a referral, turning your users into ambassadors.
              </p>
              <ul className="space-y-3">
                <li className="flex items-center space-x-3">
                  <span className="text-emerald-600">✓</span>
                  <span className="text-gray-700">One-click social sharing</span>
                </li>
                <li className="flex items-center space-x-3">
                  <span className="text-emerald-600">✓</span>
                  <span className="text-gray-700">Custom shareable links</span>
                </li>
                <li className="flex items-center space-x-3">
                  <span className="text-emerald-600">✓</span>
                  <span className="text-gray-700">Collaborative group albums</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>


      {/* CTA Section */}
      {/* <section className="py-20 bg-gradient-to-r from-indigo-600 to-emerald-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-black mb-6">
            Start Creating Your Travel Stories
          </h2>
          <p className="text-xl text-black/90 mb-8">
            Every journey deserves to be remembered and shared. Let Sarayu help you tell your story.
          </p>
          <a
            href="/contact"
            className="inline-block px-8 py-4 bg-white text-indigo-600 font-bold rounded-lg hover:shadow-lg transition-all duration-300 hover:scale-105"
          >
            Join Now
          </a>
        </div>
      </section> */}
    </div>
  );
}
